import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  RawAxiosRequestHeaders
} from 'axios'
import axios from 'axios'
import { environment } from '../../config/environment'
import { logger } from '../../utils/logger'

// Base URL for platform APIs (same host as main API; auth is via ISTIO mesh, not Bearer token)
const API_URLS = {
  local: 'http://localhost:3000',
  staging: 'https://dev.services.leadconnectorhq.com'
}

const getBaseUrl = (): string => {
  const isLocal =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      process.env.NODE_ENV === 'development')
  return isLocal ? API_URLS.local : API_URLS.staging
}

const DEFAULT_HEADERS: RawAxiosRequestHeaders = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
  version: '2021-04-15',
  'source-id': 'devtools-frontend',
  channel: 'ISTIO_MESH',
  'istio-workload-name': 'platform-devtools-frontend'
}

function handleError(error: unknown): Error {
  if (axios.isAxiosError(error) && error.response) {
    const message =
      error.response.data?.message || error.response.data?.error || 'An error occurred'
    const error_code = error.response.data?.error_code
    const customError = new Error(message)
    ;(customError as Error & { code?: string }).code = error_code
    return customError
  }
  if (axios.isAxiosError(error) && error.request) {
    return new Error('No response from server. Please check your internet connection.')
  }
  if (error instanceof Error) {
    return error
  }
  return new Error('An unexpected error occurred')
}

/**
 * Dedicated API client for platform APIs that use ISTIO mesh auth.
 * Use this for: IAM config, request filters (/platform/istio/), platform reports (/platform/reports/).
 * Does not send Authorization header; backend authenticates via mesh identity.
 * Evolves independently from the main apiClient (Authentik/Bearer).
 */
class IstioMeshApiClient {
  private client: AxiosInstance
  private baseUrl: string

  constructor() {
    this.baseUrl = getBaseUrl()
    this.client = axios.create({
      baseURL: this.baseUrl,
      timeout: environment.api.timeout,
      headers: DEFAULT_HEADERS
    })

    this.client.interceptors.request.use(
      (config) => {
        config.headers = Object.assign({}, DEFAULT_HEADERS, config.headers)
        const requestUrl =
          typeof config.url === 'string' && config.url.startsWith('http')
            ? config.url
            : `${config.baseURL || this.baseUrl}${config.url || ''}`
        logger.log('Istio mesh API request:', requestUrl)
        return config
      },
      (error) => {
        throw error
      }
    )

    this.client.interceptors.response.use(
      (response) => {
        if (response.data && Object.prototype.hasOwnProperty.call(response.data, 'data')) {
          response.data = response.data.data
        }
        return response
      },
      (error) => {
        throw handleError(error)
      }
    )
  }

  private async request<T>(config: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.request(config)
    return response.data
  }

  public async get<T>(url: string, params?: unknown, headers?: RawAxiosRequestHeaders): Promise<T> {
    return this.request<T>({ method: 'GET', url, params, headers })
  }

  public async post<T>(url: string, data?: unknown, headers?: RawAxiosRequestHeaders): Promise<T> {
    return this.request<T>({ method: 'POST', url, data, headers })
  }

  public async put<T>(url: string, data?: unknown, headers?: RawAxiosRequestHeaders): Promise<T> {
    return this.request<T>({ method: 'PUT', url, data, headers })
  }

  public async patch<T>(url: string, data?: unknown, headers?: RawAxiosRequestHeaders): Promise<T> {
    return this.request<T>({ method: 'PATCH', url, data, headers })
  }

  public async delete<T>(url: string, headers?: RawAxiosRequestHeaders): Promise<T> {
    return this.request<T>({ method: 'DELETE', url, headers })
  }
}

export const istioMeshApiClient = new IstioMeshApiClient()
